<template>
  <section class="todo-activity" :aria-busy="loading ? 'true' : 'false'">
    <button
      type="button"
      class="todo-activity-toggle"
      :aria-expanded="expanded ? 'true' : 'false'"
      @click="expanded = !expanded"
    >
      <span class="todo-activity-label">
        <template v-if="countKnown">{{ $t("activity.sectionTitleWithCount", { count: items.length }) }}</template>
        <template v-else>{{ $t("activity.sectionTitle") }}</template>
      </span>
      <i
        class="todo-activity-chevron"
        :class="expanded ? 'bi-chevron-up' : 'bi-chevron-down'"
        aria-hidden="true"
      ></i>
    </button>
    <div v-show="expanded" class="todo-activity-card">
      <p v-if="loading && !items.length" class="todo-activity-status">…</p>
      <p v-else-if="error && !items.length" class="todo-activity-status">
        {{ $t("activity.loadError") }}
        <button type="button" class="todo-activity-retry" @click="retry">
          {{ $t("activity.retry") }}
        </button>
      </p>
      <p v-else-if="!items.length" class="todo-activity-status">{{ $t("activity.empty") }}</p>
      <ul v-else class="todo-activity-list">
        <li v-for="item in items" :key="item.id" class="todo-activity-item">
          <div class="todo-activity-body" v-html="renderBody(item.body)"></div>
          <time
            v-if="item.updatedAt || item.createdAt"
            class="todo-activity-time"
            :datetime="item.updatedAt || item.createdAt"
          >{{ formatTime(item.updatedAt || item.createdAt) }}</time>
        </li>
      </ul>
      <p v-if="error && items.length" class="todo-activity-status">
        {{ $t("activity.loadError") }}
        <button type="button" class="todo-activity-retry" @click="retry">
          {{ $t("activity.retry") }}
        </button>
      </p>
      <button
        v-if="nextCursor && !error"
        type="button"
        class="todo-activity-more"
        :disabled="loading"
        @click="loadMore"
      >
        {{ $t("ui.more") }}
      </button>
    </div>
  </section>
</template>

<script>
import MarkdownIt from "markdown-it";
import moment from "moment";
import markdownTargetBlankLinks from "../helpers/markdownTargetBlankLinks";

const { fetchNotes } = require("../sync/engine");

function newestFirst(notes) {
  return notes.slice().sort((a, b) => {
    const ta = Date.parse((a && (a.updatedAt || a.createdAt)) || "") || 0;
    const tb = Date.parse((b && (b.updatedAt || b.createdAt)) || "") || 0;
    if (tb !== ta) return tb - ta;
    return String((b && b.id) || "").localeCompare(String((a && a.id) || ""));
  });
}

export default {
  name: "todoActivitySection",
  props: {
    todoId: { type: String, required: true },
  },
  data() {
    const md = new MarkdownIt({ breaks: true, linkify: true });
    markdownTargetBlankLinks.renderBlankLinks(md);
    return {
      md,
      items: [],
      nextCursor: null,
      loading: false,
      error: false,
      requestId: 0,
      expanded: false,
      countKnown: false,
    };
  },
  mounted() {
    this.onActivityUpdated = (event) => {
      const todoId = event && event.detail && event.detail.todoId;
      if (todoId && String(todoId) === String(this.todoId)) this.load();
    };
    window.addEventListener("todo-activity-updated", this.onActivityUpdated);
    this.load();
  },
  beforeUnmount() {
    if (this.onActivityUpdated) {
      window.removeEventListener("todo-activity-updated", this.onActivityUpdated);
    }
  },
  watch: {
    todoId() {
      this.expanded = false;
      this.countKnown = false;
      this.load();
    },
  },
  methods: {
    renderBody(body) {
      return this.md.render(body || "");
    },
    formatTime(iso) {
      if (!iso) return "";
      const m = moment(iso);
      if (!m.isValid()) return "";
      const now = moment();
      if (now.diff(m, "seconds") < 60) return this.$t("memos.justNow");
      if (now.isSame(m, "day")) return m.format("HH:mm");
      const locale = this.$i18n && this.$i18n.locale ? String(this.$i18n.locale) : "";
      const zh = locale.startsWith("zh");
      if (now.clone().subtract(1, "day").isSame(m, "day")) {
        return `${this.$t("memos.yesterday")} ${m.format("HH:mm")}`;
      }
      if (zh) {
        if (now.isSame(m, "year")) return m.format("M月D日 HH:mm");
        return m.format("YYYY年M月D日 HH:mm");
      }
      if (now.isSame(m, "year")) return m.format("MMM D, HH:mm");
      return m.format("MMM D, YYYY HH:mm");
    },
    retry() {
      if (this.items.length && this.nextCursor) return this.loadMore();
      return this.load();
    },
    async load() {
      const todoId = this.todoId;
      const requestId = ++this.requestId;
      this.loading = true;
      this.error = false;
      this.items = [];
      this.nextCursor = null;
      this.countKnown = false;
      if (!todoId) {
        this.loading = false;
        return;
      }
      try {
        const json = await fetchNotes({ todoId, limit: 30 });
        if (requestId !== this.requestId) return;
        const notes = json && Array.isArray(json.notes) ? json.notes : [];
        this.items = newestFirst(notes);
        this.nextCursor = (json && json.nextCursor) || null;
        this.countKnown = true;
      } catch (_) {
        if (requestId !== this.requestId) return;
        this.error = true;
        this.items = [];
        this.nextCursor = null;
        this.countKnown = false;
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    async loadMore() {
      if (!this.nextCursor || this.loading || !this.todoId) return;
      const requestId = this.requestId;
      const todoId = this.todoId;
      const cursor = this.nextCursor;
      this.loading = true;
      this.error = false;
      try {
        const json = await fetchNotes({ todoId, limit: 30, cursor });
        if (requestId !== this.requestId || todoId !== this.todoId) return;
        const notes = json && Array.isArray(json.notes) ? json.notes : [];
        const seen = new Set(this.items.map((item) => item.id));
        const merged = this.items.concat(notes.filter((note) => note && !seen.has(note.id)));
        this.items = newestFirst(merged);
        this.nextCursor = (json && json.nextCursor) || null;
        this.countKnown = true;
      } catch (_) {
        if (requestId !== this.requestId) return;
        this.error = true;
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.todo-activity {
  margin: 0 0 12px;
}

.todo-activity-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  margin: 0;
  padding: 4px 10px 4px 16px;
  border: none;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.todo-activity-label {
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.01em;
  color: #aeaeb2;
}

.dark-theme .todo-activity-label {
  color: #636366;
}

.todo-activity-chevron {
  font-size: 11px;
  color: #c7c7cc;
  line-height: 1;
}

.dark-theme .todo-activity-chevron {
  color: #636366;
}

.todo-activity-card {
  margin-top: 4px;
  background: transparent;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: none;
  border: 0.5px solid rgba(60, 60, 67, 0.1);
}

.dark-theme .todo-activity-card {
  background: transparent;
  border-color: rgba(84, 84, 88, 0.35);
}

.todo-activity-status {
  margin: 0;
  padding: 10px 14px;
  color: #aeaeb2;
  font-size: 13px;
  text-align: center;
}

.todo-activity-list + .todo-activity-status {
  border-top: 0.5px solid rgba(60, 60, 67, 0.1);
}

.todo-activity-retry {
  margin-left: 8px;
  padding: 0;
  border: none;
  background: transparent;
  color: #8e8e93;
  font-size: inherit;
  cursor: pointer;
  text-decoration: underline;
}

.todo-activity-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.todo-activity-item {
  padding: 8px 12px;
  border-bottom: 0.5px solid rgba(60, 60, 67, 0.1);

  &:last-child {
    border-bottom: none;
  }
}

.dark-theme .todo-activity-item {
  border-bottom-color: rgba(84, 84, 88, 0.35);
}

.todo-activity-body {
  font-size: 13px;
  line-height: 1.4;
  word-break: break-word;
  color: #8e8e93;
}

.dark-theme .todo-activity-body {
  color: #8e8e93;
}

.todo-activity-body :deep(p) {
  margin: 0 0 0.25em;
}

.todo-activity-body :deep(p:last-child) {
  margin-bottom: 0;
}

.todo-activity-body :deep(a) {
  color: #8e8e93;
}

.todo-activity-time {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  color: #c7c7cc;
}

.dark-theme .todo-activity-time {
  color: #636366;
}

.todo-activity-more {
  display: block;
  width: 100%;
  min-height: 36px;
  margin: 0;
  padding: 8px 12px;
  border: none;
  border-top: 0.5px solid rgba(60, 60, 67, 0.1);
  background: transparent;
  color: #aeaeb2;
  font-size: 13px;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
  }
}

.dark-theme .todo-activity-more {
  border-top-color: rgba(84, 84, 88, 0.35);
  color: #8e8e93;
}
</style>
